import * as React from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { useDataProvider, useNotify } from "react-admin";
import type { AgentDispatchRecord } from "../../models/AgentDispatchRecord";
import type { Cargo } from "../../models/Cargo";
import type { AppDataProvider } from "../../types/dataProvider";
import {
  getAgentDispatchStatusColor,
  getAgentDispatchStatusTitle,
} from "../../constants/AgentDispatchStatus";
import { getTruckPlateColorLabel } from "../../models/Truck";
import { formatDate } from "../../utils/common";
import { TruckLength, TruckType } from "../../utils/truck-utils";
import { AgentCargoInfoView, parseCargoRecord } from "./agent_cargo_info";

const HIDDEN_RAW_KEYS = new Set([
  "id",
  "_id",
  "recordId",
  "record_id",
  "status",
  "state",
  "resultStatus",
  "type",
  "actionType",
  "action_type",
  "operationType",
  "title",
  "name",
  "subject",
  "action",
  "summary",
  "description",
  "desc",
  "remark",
  "content",
  "message",
  "result",
  "output",
  "dayKey",
  "day_key",
  "date",
  "driverId",
  "driver_id",
  "driver",
  "shipperId",
  "shipper_id",
  "shipper",
  "ownerId",
  "owner_id",
  "owner",
  "cargoId",
  "cargo_id",
  "cargo",
  "waybillId",
  "waybill_id",
  "waybill",
  "truckId",
  "truck_id",
  "truck",
  "truckPlate",
  "truck_plate",
  "createdAt",
  "created_at",
  "timestamp",
  "time",
  "updatedAt",
  "updated_at",
]);

/**
 * 详情页展示用的用户信息。
 */
interface AgentUserInfo {
  id: string;
  name: string;
  realName: string;
  phone: string;
  avatar: string;
}

/**
 * 详情页展示用的车辆信息。
 */
interface AgentTruckInfo {
  id: string;
  plate: string;
  plateColor: string;
  truckType: string;
  truckLength: string;
  phone: string;
}

/**
 * 将未知值格式化为可读文本。
 */
const formatDisplayValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

/**
 * 格式化时间字段。
 */
const formatDateValue = (value: string): string => {
  if (!value) {
    return "-";
  }
  return formatDate(value);
};

/**
 * 从未知值中提取字符串 ID。
 */
const pickId = (value: unknown): string => {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  if (typeof value === "object" && value !== null) {
    const record = value as Record<string, unknown>;
    const id = record.id ?? record._id;
    if (typeof id === "string" || typeof id === "number") {
      return String(id);
    }
    if (typeof id === "object" && id !== null && "$id" in id) {
      return pickId((id as { $id: unknown }).$id);
    }
  }
  return "";
};

/**
 * 解析数字字段。
 */
const pickNumberValue = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

/**
 * 解析 fetchUserInfo / users 接口返回的用户信息。
 */
const parseUserInfo = (raw: unknown): AgentUserInfo | null => {
  let candidate: unknown = raw;
  if (Array.isArray(raw)) {
    candidate = raw[0];
  } else if (typeof raw === "object" && raw !== null) {
    const record = raw as Record<string, unknown>;
    if (Array.isArray(record.data)) {
      candidate = record.data[0];
    } else if (record.data && typeof record.data === "object") {
      candidate = record.data;
    } else if (Array.isArray(record.users)) {
      candidate = record.users[0];
    }
  }

  if (!candidate || typeof candidate !== "object") {
    return null;
  }

  const user = candidate as Record<string, unknown>;
  const id = pickId(user.id ?? user._id);
  const name =
    pickId(user.name)
    || pickId(user.nickname)
    || pickId(user.username)
    || pickId(user.fullName);
  const realName = pickId(user.real_name ?? user.realName ?? user.realname);
  const phone = pickId(user.phone ?? user.mobile ?? user.phoneNumber);
  const avatar = pickId(user.avatar ?? user.photo ?? user.headimgurl);

  if (!id && !name && !realName && !phone) {
    return null;
  }

  return {
    id,
    name: name || realName || "-",
    realName,
    phone,
    avatar,
  };
};

/**
 * 解析单条车辆记录。
 */
const parseTruckInfo = (raw: unknown): AgentTruckInfo | null => {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const truck = raw as Record<string, unknown>;
  const plate = pickId(truck.plate ?? truck.licensePlate ?? truck.license_plate);
  const typeText = pickId(truck.type);
  const truckTypeCode = pickNumberValue(truck.truck_type ?? truck.truckType);
  const truckLengthCode = pickNumberValue(truck.truck_length ?? truck.truckLength);
  const plateColorCode = pickNumberValue(truck.plate_color ?? truck.plateColor);
  const phone = pickId(truck.phone);
  const id = pickId(truck.id ?? truck._id);

  if (!id && !plate && !typeText && truckTypeCode === null) {
    return null;
  }

  return {
    id,
    plate: plate || "-",
    plateColor: plateColorCode !== null ? getTruckPlateColorLabel(plateColorCode) : "-",
    truckType: truckTypeCode !== null ? TruckType(truckTypeCode) : typeText || "-",
    truckLength: truckLengthCode !== null ? TruckLength(truckLengthCode) : "-",
    phone,
  };
};

/**
 * 从司机详情响应中解析车辆列表。
 */
const parseDriverTrucks = (raw: unknown): AgentTruckInfo[] => {
  let payload: unknown = raw;
  if (typeof raw === "object" && raw !== null && "data" in raw) {
    payload = (raw as { data: unknown }).data;
  }
  if (!payload || typeof payload !== "object") {
    return [];
  }
  const record = payload as Record<string, unknown>;
  const trucksRaw = record.trucks ?? record.Vehicles ?? record.vehicles;
  if (!Array.isArray(trucksRaw)) {
    return [];
  }
  return trucksRaw
    .map((item) => parseTruckInfo(item))
    .filter((item): item is AgentTruckInfo => item !== null);
};

/**
 * 从货源记录中解析货主 ID。
 */
const pickShipperIdFromCargo = (cargo: Cargo | null): string => {
  if (!cargo) {
    return "";
  }
  return pickId((cargo as unknown as Record<string, unknown>).owner);
};

/**
 * 详情字段展示。
 */
const DetailField: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
      {value}
    </Typography>
  </Box>
);

/**
 * 用户信息卡片（司机 / 货主）。
 */
const UserInfoCard: React.FC<{
  title: string;
  userId: string;
  user: AgentUserInfo | null;
  loading: boolean;
  error: string;
}> = ({ title, userId, user, loading, error }) => (
  <Box
    sx={{
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1.5,
      px: 1.5,
      py: 1.25,
      minWidth: 0,
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
    {loading ? (
      <Box display="flex" alignItems="center" gap={1} mt={1}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">
          正在加载...
        </Typography>
      </Box>
    ) : error ? (
      <Stack spacing={0.5} mt={0.75}>
        <Typography variant="body2" color="warning.main">
          {error}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>
          ID：{userId || "-"}
        </Typography>
      </Stack>
    ) : user ? (
      <Stack direction="row" spacing={1.25} alignItems="flex-start" mt={0.75}>
        <Avatar src={user.avatar || undefined} alt={user.name} sx={{ width: 40, height: 40 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap title={user.name}>
            {user.name}
          </Typography>
          {user.realName && user.realName !== user.name && (
            <Typography variant="caption" color="text.secondary" display="block">
              姓名：{user.realName}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" display="block">
            电话：{user.phone || "-"}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ wordBreak: "break-all" }}
          >
            ID：{user.id || userId || "-"}
          </Typography>
        </Box>
      </Stack>
    ) : (
      <Typography variant="body2" color="text.secondary" mt={0.75}>
        {userId ? `ID：${userId}` : "暂无信息"}
      </Typography>
    )}
  </Box>
);

/**
 * 司机车辆信息卡片。
 */
const DriverTrucksCard: React.FC<{
  trucks: AgentTruckInfo[];
  loading: boolean;
  error: string;
}> = ({ trucks, loading, error }) => (
  <Box
    sx={{
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1.5,
      px: 1.5,
      py: 1.25,
      minWidth: 0,
      gridColumn: "1 / -1",
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
      司机车辆
    </Typography>
    {loading ? (
      <Box display="flex" alignItems="center" gap={1} mt={1}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">
          正在加载车辆信息...
        </Typography>
      </Box>
    ) : error ? (
      <Typography variant="body2" color="warning.main" mt={0.75}>
        {error}
      </Typography>
    ) : trucks.length === 0 ? (
      <Typography variant="body2" color="text.secondary" mt={0.75}>
        暂无车辆信息
      </Typography>
    ) : (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
          gap: 1,
          mt: 0.75,
        }}
      >
        {trucks.map((truck, index) => (
          <Box
            key={truck.id || `${truck.plate}-${index}`}
            sx={{
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: 1,
              px: 1.25,
              py: 1,
              minWidth: 0,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="body2" fontWeight={600}>
                {truck.plate}
              </Typography>
              {truck.plateColor !== "-" && (
                <Chip label={truck.plateColor} size="small" variant="outlined" />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              车型：{truck.truckType} · 车长：{truck.truckLength}
            </Typography>
            {truck.phone && (
              <Typography variant="caption" color="text.secondary" display="block">
                随车电话：{truck.phone}
              </Typography>
            )}
            {truck.id && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ wordBreak: "break-all" }}
              >
                车辆 ID：{truck.id}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    )}
  </Box>
);

export interface AgentRecordDetailDialogProps {
  open: boolean;
  recordId: string;
  initialRecord?: AgentDispatchRecord | null;
  onClose: () => void;
}

/**
 * 智能体操作记录详情弹窗。
 */
export const AgentRecordDetailDialog: React.FC<AgentRecordDetailDialogProps> = ({
  open,
  recordId,
  initialRecord = null,
  onClose,
}) => {
  const dataProvider = useDataProvider() as AppDataProvider;
  const notify = useNotify();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [record, setRecord] = React.useState<AgentDispatchRecord | null>(null);
  const [cargo, setCargo] = React.useState<Cargo | null>(null);
  const [cargoLoading, setCargoLoading] = React.useState(false);
  const [cargoError, setCargoError] = React.useState("");

  const [driver, setDriver] = React.useState<AgentUserInfo | null>(null);
  const [driverLoading, setDriverLoading] = React.useState(false);
  const [driverError, setDriverError] = React.useState("");

  const [trucks, setTrucks] = React.useState<AgentTruckInfo[]>([]);
  const [trucksLoading, setTrucksLoading] = React.useState(false);
  const [trucksError, setTrucksError] = React.useState("");

  const [shipper, setShipper] = React.useState<AgentUserInfo | null>(null);
  const [shipperLoading, setShipperLoading] = React.useState(false);
  const [shipperError, setShipperError] = React.useState("");

  React.useEffect(() => {
    if (!open || !recordId) {
      return;
    }

    let cancelled = false;

    const fetchDetail = async () => {
      try {
        setLoading(true);
        setError("");
        setCargo(null);
        setCargoError("");
        setDriver(null);
        setDriverError("");
        setTrucks([]);
        setTrucksError("");
        setShipper(null);
        setShipperError("");
        if (initialRecord) {
          setRecord(initialRecord);
        } else {
          setRecord(null);
        }

        const response = await dataProvider.getAgentDispatchRecord(recordId);
        if (cancelled) {
          return;
        }
        setRecord(response.data);
      } catch (fetchError) {
        if (cancelled) {
          return;
        }
        const message =
          fetchError instanceof Error ? fetchError.message : "获取操作详情失败";
        if (!initialRecord) {
          setError(message);
        }
        notify(`获取操作详情失败：${message}`, { type: "error" });
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchDetail();

    return () => {
      cancelled = true;
    };
  }, [dataProvider, open, recordId, initialRecord, notify]);

  React.useEffect(() => {
    const cargoId = record?.cargoId;
    if (!open || !cargoId) {
      setCargo(null);
      setCargoError("");
      setCargoLoading(false);
      return;
    }

    let cancelled = false;

    const fetchCargo = async () => {
      try {
        setCargoLoading(true);
        setCargoError("");
        const response = await dataProvider.getOne("validCargos", { id: cargoId });
        if (cancelled) {
          return;
        }
        const nextCargo = parseCargoRecord(response.data);
        setCargo(nextCargo);
        if (!nextCargo) {
          setCargoError("未获取到货源信息");
        }
      } catch (fetchError) {
        if (cancelled) {
          return;
        }
        const message =
          fetchError instanceof Error ? fetchError.message : "获取货源信息失败";
        setCargo(null);
        setCargoError(message);
      } finally {
        if (!cancelled) {
          setCargoLoading(false);
        }
      }
    };

    void fetchCargo();

    return () => {
      cancelled = true;
    };
  }, [dataProvider, open, record?.cargoId]);

  /**
   * 拉取司机信息。
   */
  React.useEffect(() => {
    const driverId = record?.driverId;
    if (!open || !driverId) {
      setDriver(null);
      setDriverError("");
      setDriverLoading(false);
      return;
    }

    let cancelled = false;

    const fetchDriver = async () => {
      try {
        setDriverLoading(true);
        setDriverError("");
        const response = await dataProvider.fetchUserInfo(driverId);
        if (cancelled) {
          return;
        }
        const next = parseUserInfo(response.data);
        setDriver(next);
        if (!next) {
          setDriverError("未获取到司机信息");
        }
      } catch (fetchError) {
        if (cancelled) {
          return;
        }
        const message =
          fetchError instanceof Error ? fetchError.message : "获取司机信息失败";
        setDriver(null);
        setDriverError(message);
      } finally {
        if (!cancelled) {
          setDriverLoading(false);
        }
      }
    };

    void fetchDriver();

    return () => {
      cancelled = true;
    };
  }, [dataProvider, open, record?.driverId]);

  /**
   * 通过司机详情接口拉取车辆信息。
   */
  React.useEffect(() => {
    const driverId = record?.driverId;
    if (!open || !driverId) {
      setTrucks([]);
      setTrucksError("");
      setTrucksLoading(false);
      return;
    }

    let cancelled = false;

    const fetchTrucks = async () => {
      try {
        setTrucksLoading(true);
        setTrucksError("");
        const response = await dataProvider.getOne("drivers", { id: driverId });
        if (cancelled) {
          return;
        }
        const nextTrucks = parseDriverTrucks(response.data);
        setTrucks(nextTrucks);
      } catch (fetchError) {
        if (cancelled) {
          return;
        }
        const message =
          fetchError instanceof Error ? fetchError.message : "获取车辆信息失败";
        setTrucks([]);
        setTrucksError(message);
      } finally {
        if (!cancelled) {
          setTrucksLoading(false);
        }
      }
    };

    void fetchTrucks();

    return () => {
      cancelled = true;
    };
  }, [dataProvider, open, record?.driverId]);

  const shipperId = React.useMemo(() => {
    if (!record) {
      return "";
    }
    return record.shipperId || pickShipperIdFromCargo(cargo);
  }, [record, cargo]);

  /**
   * 拉取货主信息（优先记录中的 shipperId，否则取货源 owner）。
   */
  React.useEffect(() => {
    if (!open || !shipperId) {
      setShipper(null);
      setShipperError("");
      setShipperLoading(false);
      return;
    }

    let cancelled = false;

    const fetchShipper = async () => {
      try {
        setShipperLoading(true);
        setShipperError("");
        const response = await dataProvider.fetchUserInfo(shipperId);
        if (cancelled) {
          return;
        }
        const next = parseUserInfo(response.data);
        setShipper(next);
        if (!next) {
          setShipperError("未获取到货主信息");
        }
      } catch (fetchError) {
        if (cancelled) {
          return;
        }
        const message =
          fetchError instanceof Error ? fetchError.message : "获取货主信息失败";
        setShipper(null);
        setShipperError(message);
      } finally {
        if (!cancelled) {
          setShipperLoading(false);
        }
      }
    };

    void fetchShipper();

    return () => {
      cancelled = true;
    };
  }, [dataProvider, open, shipperId]);

  const extraFields = React.useMemo(() => {
    if (!record?.raw) {
      return [];
    }
    return Object.entries(record.raw)
      .filter(([key, value]) => !HIDDEN_RAW_KEYS.has(key) && value !== undefined && value !== null && value !== "")
      .map(([key, value]) => ({ key, value: formatDisplayValue(value) }));
  }, [record]);

  const showDriverCard = Boolean(record?.driverId);
  const showShipperCard = Boolean(shipperId) || Boolean(record?.cargoId);
  const showTrucksCard = Boolean(record?.driverId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>智能体操作详情</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box display="flex" alignItems="center" gap={1.5} py={2}>
            <CircularProgress size={22} />
            <Typography variant="body2" color="text.secondary">
              正在加载操作详情...
            </Typography>
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {!error && record && (
          <Stack spacing={2}>
            <Box
              sx={{
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.default",
                px: 2,
                py: 1.5,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ wordBreak: "break-all" }}>
                    {record.title || record.type || "未命名操作"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                    记录 ID：{record.id || recordId || "-"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  {record.type && <Chip label={record.type} size="small" variant="outlined" />}
                  {record.status && (
                    <Chip
                      label={getAgentDispatchStatusTitle(record.status)}
                      size="small"
                      color={getAgentDispatchStatusColor(record.status)}
                    />
                  )}
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                gap: 2,
              }}
            >
              <DetailField label="日期" value={record.dayKey || "-"} />
              <DetailField label="创建时间" value={formatDateValue(record.createdAt)} />
              <DetailField label="运单 ID" value={record.waybillId || "-"} />
            </Box>

            {(showDriverCard || showShipperCard || showTrucksCard) && (
              <>
                <Divider />
                <Typography variant="subtitle2">相关人员 / 车辆</Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                    gap: 1.5,
                  }}
                >
                  {showDriverCard && (
                    <UserInfoCard
                      title="司机"
                      userId={record.driverId}
                      user={driver}
                      loading={driverLoading}
                      error={driverError}
                    />
                  )}
                  {showShipperCard && (
                    <UserInfoCard
                      title="货主"
                      userId={shipperId}
                      user={shipper}
                      loading={shipperLoading || (Boolean(record.cargoId) && !shipperId && cargoLoading)}
                      error={
                        shipperError
                        || (!shipperId && !cargoLoading && record.cargoId
                          ? "货源中未找到货主信息"
                          : "")
                      }
                    />
                  )}
                  {showTrucksCard && (
                    <DriverTrucksCard
                      trucks={trucks}
                      loading={trucksLoading}
                      error={trucksError}
                    />
                  )}
                </Box>
              </>
            )}

            {record.cargoId && (
              <>
                <Divider />
                <Typography variant="subtitle2">货源信息</Typography>
                {cargoError && (
                  <Alert severity="warning">
                    {cargoError}
                  </Alert>
                )}
                <AgentCargoInfoView
                  cargo={cargo}
                  loading={cargoLoading}
                  emptyText="暂无货源详情"
                />
              </>
            )}

            {(record.summary || record.content) && (
              <>
                <Divider />
                {record.summary && <DetailField label="摘要" value={record.summary} />}
                {record.content && <DetailField label="内容" value={record.content} />}
              </>
            )}

            {extraFields.length > 0 && (
              <>
                <Divider />
                <Typography variant="subtitle2">其他字段</Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                    gap: 2,
                  }}
                >
                  {extraFields.map((field) => (
                    <DetailField key={field.key} label={field.key} value={field.value} />
                  ))}
                </Box>
              </>
            )}
          </Stack>
        )}
        {!loading && !error && !record && (
          <Typography variant="body2" color="text.secondary">
            暂无详情数据
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>关闭</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgentRecordDetailDialog;
